import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Grid, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import Loader from '../../common/Loader'
import MerchantSelect from './MerchantSelect'
import { downloadCsv } from './allocationExport'
import { fetchAnalytics, fetchAnalyticsCsv } from './paceAnalyticsService'

// LIVE and SHADOW rows answer different questions (PACE+ §2D), so every view is for one mode.
// Live: PACE decided, the rules-only (manual) courier is the baseline. Shadow: the rules decided.
const TABS = [
  { view: 'adherence', label: 'Adherence' },
  { view: 'commonality', label: 'PACE vs Manual' },
  { view: 'whatif', label: { SHADOW: 'What if PACE goes live', LIVE: 'PACE vs rules' } },
  { view: 'outcomes', label: 'Outcomes' },
  { view: 'inputs', label: 'Input audit' },
]
const tabLabel = (t, mode) => (typeof t.label === 'string' ? t.label : t.label[mode])

// From / To are picked in the browser's local time (IST for ops) as 'YYYY-MM-DDTHH:MM' and sent
// to PACE as the UTC minute it stores created_at in. To is inclusive of its minute.
const pad = (n) => String(n).padStart(2, '0')
const localDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const daysAgoStart = (n) => `${localDay(new Date(Date.now() - n * 86400000))}T00:00`
const todayEnd = () => `${localDay(new Date())}T23:59`
const toUtcMinute = (local) => (local ? new Date(local).toISOString().slice(0, 16) : undefined)
const fmt = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`)
const signed = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}${suffix}`)

function Stat({ label, value, hint }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={600}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  )
}

function Stats({ items }) {
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {items.map((s) => <Grid item xs={12} sm={6} md={3} key={s.label}><Stat {...s} /></Grid>)}
    </Grid>
  )
}

/** columns: [{ key, label, render? }] */
function SimpleTable({ title, columns, rows }) {
  return (
    <Box sx={{ mb: 3 }}>
      {title && <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{title}</Typography>}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>{columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={columns.length} align="center">No data for these filters</TableCell></TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={i}>
                {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(r) : fmt(r[c.key])}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const kv = (obj) => Object.entries(obj || {}).map(([k, v]) => ({ k, v }))
const KV_COLS = (a, b) => [{ key: 'k', label: a }, { key: 'v', label: b }]

function Adherence({ d }) {
  const b = d.booking_record
  const booked = b.booked || 0
  const live = d.filters.mode === 'LIVE'
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{b.note}</Typography>
      <Stats items={[
        {
          label: live ? 'PACE recommendation booked' : 'Rules booked PACE\'s pick (agreement)',
          value: fmt(b.pace_pick_booked_pct, '%'), hint: `of ${booked} booked allocations`,
        },
        ...d.shipped_vs_pace_pick.map((r) => ({
          label: 'Shipped with PACE pick', value: fmt(r.shipped_with_pace_pick_pct, '%'),
          hint: `${r.orders} shipped orders, last 60 days`,
        })),
      ]} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SimpleTable title="Booked courier's rank in PACE's list" columns={KV_COLS('Rank', 'Allocations')}
            rows={kv(b.booked_rank_in_pace)} />
        </Grid>
        {/* on Shadow the answer is always "the rules booked another courier" */}
        {live && (
          <Grid item xs={12} md={4}>
            <SimpleTable title="Why PACE's pick wasn't booked" columns={KV_COLS('Stage', 'Allocations')}
              rows={kv(b.why_pace_pick_not_booked)} />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <SimpleTable title="Allocations by booking status" columns={KV_COLS('Status', 'Allocations')}
            rows={kv(b.allocations_by_status)} />
        </Grid>
      </Grid>
      <SimpleTable title="Daily: shipped with PACE pick" rows={d.trend} columns={[
        { key: 'day', label: 'Day' }, { key: 'orders', label: 'Shipped orders' },
        { key: 'shipped_with_pace_pick_pct', label: 'With PACE pick', render: (r) => fmt(r.shipped_with_pace_pick_pct, '%') },
      ]} />
    </>
  )
}

function Commonality({ d }) {
  const c = d.commonality
  const g = d.pace_minus_manual
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{d.note}</Typography>
      <Stats items={[
        { label: 'Same courier (PACE #1 = manual)', value: fmt(c.same_courier_pct, '%'), hint: `${d.allocations} allocations` },
        { label: 'Courier-mix overlap', value: fmt(c.courier_mix_overlap_pct, '%'), hint: 'Σ min(PACE share, manual share)' },
        { label: 'Δ freight (PACE − manual)', value: signed(g.quote_inr, ' ₹'), hint: 'exact, from quotes · per allocation' },
        { label: 'Δ ETA (PACE − manual)', value: signed(g.eta_h, ' h'), hint: 'PACE estimate' },
      ]} />
      <Stats items={[
        { label: 'Δ Delivery %', value: signed(g.delivery_pts_est, ' pts'), hint: 'PACE estimate' },
        { label: 'Δ FASR', value: signed(g.fasr_pts_est, ' pts'), hint: 'PACE estimate' },
        { label: 'Δ FATA', value: signed(g.fata_pts_est, ' pts'), hint: 'PACE estimate' },
      ]} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SimpleTable title="Manual courier's rank in PACE's list" columns={KV_COLS('Rank', 'Allocations')}
            rows={kv(c.manual_courier_rank_in_pace)} />
        </Grid>
        <Grid item xs={12} md={8}>
          <SimpleTable title="Courier mix" rows={d.courier_mix} columns={[
            { key: 'prozo_id', label: 'Courier' },
            { key: 'pace_pick_pct', label: 'PACE pick', render: (r) => fmt(r.pace_pick_pct, '%') },
            { key: 'manual_pct', label: 'Manual', render: (r) => fmt(r.manual_pct, '%') },
          ]} />
        </Grid>
      </Grid>
      <SimpleTable title="By merchant" rows={d.by_merchant} columns={[
        { key: 'merchant_id', label: 'Merchant' }, { key: 'allocations', label: 'Allocations' },
        { key: 'same_courier_pct', label: 'Same courier', render: (r) => fmt(r.same_courier_pct, '%') },
        { key: 'quote_diff_inr', label: 'Δ freight ₹', render: (r) => signed(r.quote_diff_inr) },
        { key: 'eta_diff_h', label: 'Δ ETA h (est.)', render: (r) => signed(r.eta_diff_h) },
      ]} />
    </>
  )
}

const OUTCOME_LABELS = { delivered: 'Delivered %', rto: 'RTO %', fasr: 'FASR', fata: 'FATA (first attempt within EDD)' }

function WhatIf({ d }) {
  const p = d.provenance
  const c = d.compare
  const alt = d.filters.mode === 'LIVE' ? 'If rules' : 'If PACE'
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{d.note}</Typography>
      <Stats items={[
        { label: 'Orders where the courier would change', value: d.unchanged_pct == null ? '—' : `${Math.round((100 - d.unchanged_pct) * 10) / 10}%`, hint: `of ${d.orders} shipped orders` },
        { label: 'Δ freight per order (PACE − rules)', value: signed(d.freight.delta_per_order_inr, ' ₹'), hint: `exact · ${d.freight.orders_with_both_quotes} orders with both quotes` },
        { label: 'Δ freight total (PACE − rules)', value: signed(d.freight.delta_total_inr, ' ₹'), hint: 'exact, from the same allocation snapshot' },
        { label: 'Δ ETA (PACE − rules)', value: signed(d.eta_delta_h, ' h'), hint: 'PACE estimate' },
      ]} />
      <SimpleTable title={`Outcomes: ${c.today} vs ${c.alternative}`}
        rows={Object.entries(d.outcomes).map(([k, v]) => ({ k: OUTCOME_LABELS[k] || k, ...v }))}
        columns={[
          { key: 'k', label: 'Metric' },
          { key: 'today_actual_pct', label: 'Today (actual)', render: (r) => fmt(r.today_actual_pct, '%') },
          { key: 'alt_est_pct', label: `${alt} (estimated)`, render: (r) => fmt(r.alt_est_pct, '%') },
          { key: 'delta_pts', label: 'Δ PACE − rules', render: (r) => signed(r.delta_pts, ' pts') },
        ]} />
      <Alert severity="info" sx={{ mb: 3 }}>
        <b>How the estimates were arrived at.</b> {p.text}
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SimpleTable title="Courier mix shift" rows={d.courier_mix} columns={[
            { key: 'prozo_id', label: 'Courier' },
            { key: 'today_pct', label: 'Today', render: (r) => fmt(r.today_pct, '%') },
            { key: 'alt_pct', label: alt, render: (r) => fmt(r.alt_pct, '%') },
          ]} />
        </Grid>
        <Grid item xs={12} md={6}>
          <SimpleTable title="Estimate source" columns={KV_COLS('Source', 'Orders')}
            rows={kv(p.by_source).map((r) => ({ ...r, k: SOURCE_LABELS[r.k] || r.k }))} />
        </Grid>
      </Grid>
      <SimpleTable title="By merchant" rows={d.by_merchant} columns={[
        { key: 'merchant_id', label: 'Merchant' }, { key: 'orders', label: 'Orders' },
        { key: 'unchanged_pct', label: 'Same courier', render: (r) => fmt(r.unchanged_pct, '%') },
        { key: 'freight_delta_inr', label: 'Δ freight ₹ (PACE − rules)', render: (r) => signed(r.freight_delta_inr) },
        { key: 'eta_delta_h', label: 'Δ ETA h', render: (r) => signed(r.eta_delta_h) },
        { key: 'delivered_delta_pts', label: 'Δ delivered pts (est.)', render: (r) => signed(r.delivered_delta_pts) },
        { key: 'rto_delta_pts', label: 'Δ RTO pts (est.)', render: (r) => signed(r.rto_delta_pts) },
        { key: 'lane_week_pct', label: 'From lane × week', render: (r) => fmt(r.lane_week_pct, '%') },
        { key: 'prediction_pct', label: 'From prediction', render: (r) => fmt(r.prediction_pct, '%') },
      ]} />
    </>
  )
}

const SOURCE_LABELS = {
  same_courier: "Order's own outcome (that courier shipped)",
  lane_week: 'Lane × week actuals (n ≥ 30)',
  prediction: "PACE's prediction (n < 30)",
  none: 'No basis',
}

function Outcomes({ d }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{d.note}</Typography>
      <SimpleTable title="Shipped with" rows={d.by_courier_choice} columns={[
        { key: 'shipped_with', label: 'Courier shipped' }, { key: 'orders', label: 'Orders' },
        { key: 'delivered_pct', label: 'Delivered', render: (r) => fmt(r.delivered_pct, '%') },
        { key: 'rto_pct', label: 'RTO', render: (r) => fmt(r.rto_pct, '%') },
        { key: 'first_attempt_pct', label: 'FASR', render: (r) => fmt(r.first_attempt_pct, '%') },
        { key: 'on_time_pct', label: 'On time', render: (r) => fmt(r.on_time_pct, '%') },
        { key: 'avg_tat_h', label: 'Avg TAT h' },
        { key: 'avg_margin_inr', label: 'Avg margin ₹' },
      ]} />
      <SimpleTable title="Orders by match status" columns={KV_COLS('Status', 'Orders')} rows={kv(d.orders_by_match_status)} />
    </>
  )
}

function Inputs({ d }) {
  const share = (k) => (r) => (r.effective_share_pct ? fmt(r.effective_share_pct[k], '%') : '—')
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{d.note}</Typography>
      <SimpleTable rows={d.knob_sets} columns={[
        { key: 'merchant_id', label: 'Merchant' }, { key: 'payment_scope', label: 'Payment' },
        { key: 'first_seen', label: 'First seen' }, { key: 'last_seen', label: 'Last seen' },
        { key: 'allocations', label: 'Allocations' },
        { key: 'delivery', label: 'Delivery %', render: share('delivery') },
        { key: 'fasr', label: 'FASR', render: share('fasr') },
        { key: 'fata', label: 'FATA', render: share('fata') },
        { key: 'logistics', label: 'Logistics', render: share('logistics') },
        { key: 'alpha', label: 'α speed↔cost' },
        { key: 'min_delivery_prob', label: 'Min delivery %' },
        { key: 'risk_weight', label: 'Risk weight' },
        { key: 'kci_enabled', label: 'KCI' },
      ]} />
    </>
  )
}

const MODE_NAME = { LIVE: 'Live', SHADOW: 'Shadow' }

/** Empty Live/Shadow view: say which mode the merchant actually runs in, instead of empty tables. */
function NoRowsInMode({ mode, rows, merchant, onSwitch }) {
  const other = mode === 'LIVE' ? 'SHADOW' : 'LIVE'
  if (!rows[other]) {
    return (
      <Alert severity="warning">
        {merchant
          ? 'This merchant has no PACE allocations in this period — PACE is not being called for its orders.'
          : 'No PACE allocations in this period.'}
      </Alert>
    )
  }
  return (
    <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => onSwitch(other)}>Show {MODE_NAME[other]}</Button>}>
      {merchant
        ? <>This merchant runs in <b>{MODE_NAME[other]}</b> — no {MODE_NAME[mode]} allocations in this period ({rows[other].toLocaleString()} {MODE_NAME[other]}).</>
        : <>No merchant ran in {MODE_NAME[mode]} in this period.</>}
    </Alert>
  )
}

const VIEWS = { adherence: Adherence, commonality: Commonality, whatif: WhatIf, outcomes: Outcomes, inputs: Inputs }

/**
 * PACE → Analytics (PACE+ §2): adherence to PACE's pick, PACE vs the rules-only
 * (manual) courier, PACE vs rules (what if), real outcomes by courier choice, and the knobs
 * applied — always for one mode, Live or Shadow (§2D), never both blended.
 * Read-only; numbers computed by PACE nightly. Internal only.
 */
export default function PaceAnalyticsPage() {
  const { enqueueSnackbar } = useSnackbar()
  const [tab, setTab] = useState(0)
  const [merchant, setMerchant] = useState(null)
  const [since, setSince] = useState(daysAgoStart(30))
  const [until, setUntil] = useState(todayEnd())
  const [mode, setMode] = useState('SHADOW')
  const [paymentMode, setPaymentMode] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const cache = useRef(new Map())
  const [modes, setModes] = useState(new Map())   // merchantId → { current_mode, LIVE, SHADOW }

  // which merchants run Live / Shadow in the period — tags the merchant picker; optional
  useEffect(() => {
    fetchAnalytics('merchants', { since: toUtcMinute(since), until: toUtcMinute(until) })
      .then((r) => setModes(new Map((r.data?.merchants || []).map((m) => [String(m.merchant_id), m]))))
      .catch(() => setModes(new Map()))
  }, [since, until])
  const tagFor = useCallback((m) => {
    const x = modes.get(String(m.merchantId))
    if (!x) return null
    return x.current_mode === 'LIVE'
      ? { label: 'Live', color: 'success', rank: 0 }
      : { label: 'Shadow', color: 'default', rank: 1 }
  }, [modes])

  const filters = {
    merchant_id: merchant?.merchantId, since: toUtcMinute(since), until: toUtcMinute(until),
    mode, payment_mode: paymentMode,
  }
  const view = TABS[tab].view
  const key = JSON.stringify([view, filters])

  const load = useCallback(() => {
    // same tab + filters seen this visit → show it at once (data refreshes at most hourly)
    const hit = cache.current.get(key)
    if (hit) { setData({ view, body: hit }); setError(null); return }
    setLoading(true)
    setError(null)
    fetchAnalytics(view, filters)
      .then((r) => { cache.current.set(key, r.data); setData({ view, body: r.data }) })
      .catch((e) => setError(e?.response?.data?.error || e?.response?.data?.detail?.[0]?.msg || 'Could not load analytics'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(load, [load])

  // kind: 'rows' = raw columns (export.csv) · 'business' = the ops layout (export_business.csv)
  const exportCsv = (kind) => {
    setExporting(kind)
    const [file, prefix] = kind === 'business'
      ? ['export_business.csv', 'pace-business'] : ['export.csv', 'pace-analytics']
    fetchAnalyticsCsv(filters, file)
      .then((r) => downloadCsv(`${prefix}_${mode.toLowerCase()}_${since}_${until}.csv`.replace(/:/g, ''), r.data))
      .catch(() => enqueueSnackbar('Export failed', { variant: 'error' }))
      .finally(() => setExporting(false))
  }

  const View = VIEWS[view]
  return (
    <div>
      <PageTitle>PACE · Analytics</PageTitle>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}><MerchantSelect value={merchant} onChange={setMerchant} label="Merchant (all)" tagFor={modes.size ? tagFor : undefined} /></Grid>
          <Grid item xs={6} md={2}>
            <TextField type="datetime-local" size="small" fullWidth label="From" value={since}
              onChange={(e) => e.target.value && setSince(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="datetime-local" size="small" fullWidth label="To" value={until}
              onChange={(e) => e.target.value && setUntil(e.target.value)} InputLabelProps={{ shrink: true }}
              helperText="your local time · inclusive" />
          </Grid>
          <Grid item xs={6} md={2}>
            <ToggleButtonGroup exclusive size="small" fullWidth color="primary" value={mode}
              onChange={(_e, v) => v && setMode(v)}>
              <ToggleButton value="SHADOW">Shadow</ToggleButton>
              <ToggleButton value="LIVE">Live</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select size="small" fullWidth label="Payment" value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}>
              <MenuItem value="">All</MenuItem><MenuItem value="COD">COD</MenuItem><MenuItem value="PREPAID">Prepaid</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>
      <Alert severity="info" sx={{ mb: 2 }}>
        {mode === 'LIVE'
          ? <><b>Live merchants</b>: PACE chose the courier. Compared against the rules-only (manual) courier recorded for each order.</>
          : <><b>Shadow merchants</b>: the rules chose the courier; PACE ranked alongside without effect. This is the go-live view.</>}
        {' '}Live and Shadow merchants differ, so compare each mode on its own, not with each other.
      </Alert>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 2 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          {TABS.map((t) => <Tab key={t.view} label={tabLabel(t, mode)} />)}
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="One row per allocation in the business layout: IST times, merchant name, service types, PACE vs manual quote / ETA / probabilities, booking and outcome. Rate-card RTO charge and landed cost show NA (not available yet).">
            <span>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => exportCsv('business')}
                disabled={!!exporting}>
                {exporting === 'business' ? 'Exporting…' : 'Export CSV (business)'}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportCsv('rows')} disabled={!!exporting}>
            {exporting === 'rows' ? 'Exporting…' : 'Export CSV (rows)'}
          </Button>
        </Box>
      </Box>
      {loading && <Loader />}
      {error && <Alert severity="error">{String(error)}</Alert>}
      {!loading && !error && data?.view === view && data.body?.data_until && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          Data up to {data.body.data_until.slice(0, 16)} UTC · refreshed nightly, new orders added within about an hour
        </Typography>
      )}
      {!loading && !error && data?.view === view && (
        data.body?.filters?.rows_by_mode && !data.body.filters.rows_by_mode[mode] && view !== 'inputs'
          ? <NoRowsInMode mode={mode} rows={data.body.filters.rows_by_mode} merchant={merchant} onSwitch={setMode} />
          : <View d={data.body} />
      )}
    </div>
  )
}
